import * as React from 'react';
import { Autocomplete, Box, Button, Typography, FormControl, FormLabel, Select, Option, Accordion, Drawer, Stack, AccordionSummary, AccordionDetails, IconButton, Input } from '@mui/joy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import InputFileUpload from '../components/InputFileUpload';

export default function CreateTaskDrawer({
  loading,
  open,
  onClose,
  policies = [],
  pages = [],
  selectedFiles = [],
  setSelectedFiles,
  handleSubmit,
  currentPolicy = null,
  handleFileChange,
  setCurrentPolicy,
  currentOutputPolicy = null,
  setCurrentOutputPolicy,
  referencedObjects,
  setReferencedObjects
}) {
  const [expandedIndex, setExpandedIndex] = React.useState(null);

  const handleRemoveFile = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  // Separate regular policies and output policies
  const outputPolicies = policies.filter(policy => policy.settings?.type === 'Output');
  const regularPolicies = policies.filter(policy => policy.settings?.type !== 'Output');

  const applySameReferenceData = () => {
    if (selectedFiles.length < 2) return;

    const openedFileName = selectedFiles[expandedIndex]?.name;
    const openedFileData = referencedObjects[openedFileName] || {};

    setReferencedObjects((prev) => {
      const newObjects = { ...prev };
      selectedFiles.forEach((file) => {
        newObjects[file.name] = { ...openedFileData };
      });
      return newObjects;
    });

  };

  const hasMeaningfulReferenceData = currentPolicy?.policy?.ReferencedObjects?.some(obj => obj.key && obj.key.trim() !== "");
  
  React.useEffect(() => {
    if (selectedFiles.length > 0) {
      setExpandedIndex(0);
    }
  }, [selectedFiles]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ p: 2, flexGrow: 1, overflowY: 'auto' }}>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <Typography level='h4'>Test Policy</Typography>
        </Stack>
        <Typography>Please choose a policy and upload a document. Output policy is optional.</Typography>

        {/* Regular Policy Selection */}
        <FormControl sx={{ mt: 1 }} required>
          <FormLabel>Policy:</FormLabel>
          <Autocomplete
            options={regularPolicies}
            getOptionLabel={(option) => option?.id ? `${option.id} | ${option.name} | ${option.settings?.type || 'Policy'}` : ''}
            value={currentPolicy}
            onChange={(_, newPolicy) => {
              setCurrentPolicy(newPolicy)
              setReferencedObjects({})
            }}
            placeholder="Select a policy..."
            isOptionEqualToValue={(option, value) => option.id === value?.id}
          />
        </FormControl>

        {currentPolicy?.settings?.type?.toLowerCase() !== 'evaluation' && (
        <FormControl sx={{ mt: 1 }}>
          <FormLabel>Output Policy:</FormLabel>
          <Autocomplete
            options={outputPolicies}
            getOptionLabel={(option) => option?.id ? `${option.id} | ${option.name} | ${option.settings?.type || 'Policy'}` : ''}
            value={currentOutputPolicy}
            onChange={(_, newPolicy) => setCurrentOutputPolicy(newPolicy)}
            placeholder="Select an output policy..."
            isOptionEqualToValue={(option, value) => option.id === value?.id}
          />
        </FormControl>
        )}

        {/* File Upload or Document Selection */}
        <FormControl required sx={{ mt: 1 }}>
          <FormLabel>Files:</FormLabel>
          <Box sx={{ mt: 1 }}>
            {currentPolicy?.settings?.type?.toLowerCase() === 'evaluation' ? (
              <Select
                placeholder="Select a document..."
                onChange={(e, newValue) => {
                  if (!newValue) return;
                  const selectedDoc = currentPolicy.policy.documents.find(doc => doc.id === newValue);
                  if (selectedDoc) {
                    // Check if already selected to avoid duplicates if needed, 
                    // or allow multiple selections. For now, let's assume single selection 
                    // or append to list. The original UI supports multiple files.
                    // Let's treat this as adding a file to the list.
                    
                    // Check if already in selectedFiles
                    if (!selectedFiles.some(f => f.id === selectedDoc.id)) {
                        const newFile = {
                            ...selectedDoc,
                            name: selectedDoc.name,
                            // Add a flag to distinguish from regular files
                            isEvaluationDocument: true
                        };
                        setSelectedFiles(prev => [...prev, newFile]);
                        
                        // Pre-fill reference data from documentFields
                        if (selectedDoc.documentFields) {
                            setReferencedObjects(prev => ({
                                ...prev,
                                [selectedDoc.name]: selectedDoc.documentFields
                            }));
                        }
                    }
                  }
                }}
              >
                {currentPolicy.policy?.documents?.map((doc) => (
                  <Option key={doc.id} value={doc.id}>
                    {doc.name}
                  </Option>
                ))}
              </Select>
            ) : (
              selectedFiles.length === 0 && (
                <InputFileUpload onChange={handleFileChange} selectedFiles={selectedFiles} />
              )
            )}

            {selectedFiles.length > 0 && (
            <>
            {hasMeaningfulReferenceData && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button onClick={applySameReferenceData} disabled={selectedFiles.length < 2}>
                    Apply Same Reference Data
                  </Button>
                </Box>
            )}
              {selectedFiles.map((file, index) => (
                <Accordion
                  key={index}
                  expanded={expandedIndex === index && hasMeaningfulReferenceData}
                  onChange={(event, expanded) => setExpandedIndex(expanded ? index : null)}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ display: 'flex', alignItems: 'start' }}>
                    <IconButton size="sm" color="danger" sx={{ mr: 1 }} onClick={() => handleRemoveFile(index)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    <Typography
                      level="body2"
                      sx={{
                        flexGrow: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {file.name}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails variant="soft">
                    {hasMeaningfulReferenceData ? (
                      <Box sx={{ mt: 2, p: 2 }}>
                        <Typography level="h4">Reference Data:</Typography>
                        <Typography>Please fill in the reference data regarding this document.</Typography>
                        {currentPolicy.policy.ReferencedObjects.map((field) => (
                          <FormControl key={field.key} required sx={{ marginInline: 4, mt: 1 }}>
                            <FormLabel sx={{ textTransform: "capitalize" }}>{field.key}</FormLabel>
                            <Input
                              value={referencedObjects[file.name]?.[field.key] || ""}
                              onChange={(event) =>
                                setReferencedObjects({
                                  ...referencedObjects,
                                  [file.name]: {
                                    ...referencedObjects[file.name],
                                    [field.key]: event.target.value,
                                  },
                                })
                              }
                            />
                          </FormControl>
                        ))}
                      </Box>
                    ) : (
                      <Typography sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
                        No reference data available.
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
            )}
          </Box>
        </FormControl>
      </Box>

      {/* Start Button (Updated Condition) */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'neutral.outlinedBorder', position: 'sticky', bottom: 0, bgcolor: 'background.surface' }}>
        <Button
          loading={loading}
          disabled={selectedFiles.length === 0 || !currentPolicy}
          onClick={handleSubmit}
          sx={{ width: "100%" }}
        >
          Start
        </Button>
      </Box>
    </Drawer>
  );
}
